"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import * as SocialIcons from "@/components/assets_old/SocialIcons";
import { Button } from "@/components/input/Button";
import { analytics } from "@/services/Analytics";
import { trpc, type RouterInput } from "@/services/Trpc/client";
import { useUserIsSubscribed } from "@/utils/auth/useUserIsSubscribed";
import { useForm } from "react-hook-form";

import { SubscribeModal } from "../SubscribeModal";
import Modal from "./Modal/Modal";

interface Props {
  showSend: boolean;
  setShowSend: React.Dispatch<boolean>;
  cardId: string;
  cardName: string;
  cardThumbnailUrl: string;
  isFree: boolean;
}

const SendCardModal = (props: Props) => {
  const subscribed = useUserIsSubscribed();

  if (!props.showSend) return null;
  if (subscribed || props.isFree)
    return (
      <SendCardModalInternal
        isFree={props.isFree}
        showSend={props.showSend}
        setShowSend={props.setShowSend}
        cardId={props.cardId}
        cardName={props.cardName}
        cardThumbnailUrl={props.cardThumbnailUrl}
      />
    );
  else
    return (
      <SubscribeModal
        visible={props.showSend}
        hide={() => props.setShowSend(false)}
      />
    );
};

type SendFormInputs = {
  name: string;
  email: string;
  recipientName: string;
  recipientEmail: string;
  yourName: string;
};

function constructMessage(data: SendFormInputs, shareUrl: URL) {
  let message = "";
  if (data.recipientName.length > 0) {
    message += `Dear ${data.recipientName},\r\n`;
  }
  message += `I just sent you a card on Wrap.Gifts! \r\nCheck it out at ${shareUrl.toString()}!`;
  if (data.yourName.length > 0) {
    message += `\r\n${data.yourName}`;
  }
  return message;
}

function createMailtoLink(data: SendFormInputs, shareUrl: URL) {
  const url = new URL(`mailto:${data.recipientEmail}`);
  // https://stackoverflow.com/questions/59889140/different-output-from-encodeuricomponent-vs-urlsearchparams
  // using search params doesn't work for encoding url
  const subject = encodeURIComponent(
    `You received a Card from ${data.yourName}`,
  );
  const body = encodeURIComponent(constructMessage(data, shareUrl));

  const link = url.toString() + "?subject=" + subject + "&body=" + body;
  return link;
}

const SendCardModalInternal = ({ showSend, setShowSend, cardId }: Props) => {
  const { mutate: sendCard, isLoading } = trpc.card.send.useMutation();
  const { register, handleSubmit, watch } = useForm<SendFormInputs>({
    defaultValues: {
      name: "",
      email: "",
      recipientEmail: "",
      recipientName: "",
      yourName: "",
    },
  });

  const formData = watch();

  const [message, setMessage] = useState("");
  const [copied, setCopied] = useState(false);
  const shareUrl = useMemo(() => {
    const url = new URL(window.location.origin);
    url.pathname = "view";
    url.searchParams.append("id", cardId);
    return url;
  }, [cardId]);

  useEffect(() => {
    setMessage(constructMessage(formData, shareUrl));
  }, [formData, shareUrl]);

  const handleSend = useCallback(
    (
      method: RouterInput["card"]["send"]["sendMethod"],
      data: SendFormInputs,
    ) => {
      if (isLoading) return;
      const email =
        data.recipientEmail.length > 0 ? data.recipientEmail : undefined;
      sendCard({
        cardId: cardId,
        recipient: {
          name: data.recipientName,
          email: email,
        },
        sendMethod: method,
      });
      analytics.track("sendCard").catch(console.error);
    },
    [cardId, isLoading, sendCard],
  );

  const handleSendWithEmail = useCallback(
    () => handleSubmit((data) => handleSend("email", data))(),
    [handleSend, handleSubmit],
  );

  const handleCopySend = useCallback(
    () =>
      handleSubmit((data) => {
        setCopied(true);
        navigator.clipboard.writeText(message).catch(console.error);
        analytics.track("copyCardUrl").catch(console.error);
        handleSend("link", data);
      })(),
    [handleSend, handleSubmit, message],
  );

  const handleMailtoSend = useCallback(
    () =>
      handleSubmit((data) => {
        const link = createMailtoLink(data, shareUrl);

        window.open(link, "_blank", "noreferrer");
        handleSend("link", data);
      })(),
    [handleSend, handleSubmit, shareUrl],
  );

  return (
    <Modal
      show={showSend}
      setShow={setShowSend}
      onClose={() => setCopied(false)}
      header={<Modal.Header>Send card</Modal.Header>}
      body={
        <Modal.Body className="flex flex-col space-y-4">
          <form className="flex flex-col" onSubmit={(e) => e.preventDefault()}>
            <span className="text-center text-lg font-bold">
              First, tell us who to send your card to
            </span>
            <span className="mt-1 font-medium">Name:</span>
            <input
              {...register("recipientName")}
              className="  rounded-lg bg-gray-200 p-2 pl-2"
              placeholder="Recipient Name"
            />
            <span className="mt-1 font-medium">Email:</span>
            <input
              className="rounded-lg bg-gray-200 p-2 pl-2"
              placeholder="email@example.com"
              {...register("recipientEmail")}
            />
            <span className="pt-2 text-center text-lg font-bold">
              Sign your card
            </span>
            <span className="-mt-2 font-medium">Your Name:</span>
            <input
              {...register("yourName")}
              className="  rounded-lg bg-gray-200 p-2 pl-2"
              placeholder="Your Name"
            />
            <span className="pb-2 pt-2 text-center text-lg font-bold">
              Preview
            </span>
            <textarea
              className="grid h-40 resize-none rounded bg-gray-200 p-2 text-sm"
              value={message}
              disabled
            />
          </form>

          <div className="flex gap-2">
            <Button
              className="flex w-full items-center justify-center gap-2"
              onClick={() => void handleMailtoSend()}
            >
              Email
            </Button>
            <Button className="w-full " onClick={() => handleCopySend()}>
              {copied ? "Copied!" : "Copy To Clipboard"}
            </Button>
          </div>
        </Modal.Body>
      }
    />
  );
};

export default SendCardModal;

const _Socials = () => {
  return (
    <div className=" flex flex-col">
      <span className="text-center text-lg font-bold">
        Want to share your experience?
      </span>
      <div className="flex justify-center space-x-5 pt-4">
        <Button variant="purple" size="lg" onClick={openFaceBook}>
          <SocialIcons.FacebookIcon />
        </Button>
        <Button variant="purple" size="lg" onClick={openMessenger}>
          <SocialIcons.MessengerIcon />
        </Button>
        <Button variant="purple" size="lg" onClick={openTwitter}>
          <SocialIcons.TwitterIcon />
        </Button>
        <Button variant="purple" size="lg" onClick={openInstagram}>
          <SocialIcons.InstagramIcon />
        </Button>
      </div>
    </div>
  );
};
function openTwitter() {
  return window.open(
    "https://twitter.com/intent/tweet?text=I%20just%20sent%20a%20customized%20virtual%20gift%20card%20with%20Wrap.Gifts%21",
  );
}
function openMessenger() {
  return window.open("https://www.m.me");
}
function openFaceBook() {
  //
  return window.open(
    "https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2Fwrap.gifts",
  );
}
function openFaceBookAlternative() {
  return window.open(
    "https://www.facebook.com/dialog/share?app_id=145634995501895&display=popup&href=https%3A%2F%2Fdevelopers.facebook.com%2Fdocs%2F&redirect_uri=https%3A%2F%2Fdevelopers.facebook.com%2Ftools%2Fexplorer",
  );
}
function openInstagram() {
  return window.open("https://www.instagram.com/create/story");
}
